import { DateTime } from 'luxon';

import { DateChangeEmailData } from './DateChangeEmailPreview';

export interface DateChangePreviewInput {
	eventName: string;
	eventId?: string | number;
	startDate?: Date | null;
	endDate?: Date | null;
	timeZone?: string;
	venueName?: string;
	/** Optional branding pulled from the event so the preview matches the real email. */
	companyLogoUrl?: string;
	eventBanner?: string;
	primaryColor?: string;
	titleColor?: string;
	webpageUrl?: string;
}

const fmt = (date: Date | null | undefined, format: string): string => {
	if (!date || isNaN(date.getTime())) return '';
	return DateTime.fromJSDate(date).toFormat(format);
};

// Builds the handlebars data for the email preview from the live form values + event branding. Formats
// dates/times exactly as the backend does ("Mon D" / "hh:mm a") so the client-side preview matches the
// email SendGrid produces from the same values. `customMessage` is injected by the dialog as the user types.
export const buildDateChangePreview = ({
	eventName,
	eventId,
	startDate,
	endDate,
	timeZone,
	venueName,
	companyLogoUrl,
	eventBanner,
	primaryColor,
	titleColor,
	webpageUrl,
}: DateChangePreviewInput): Omit<DateChangeEmailData, 'customMessage'> => {
	const startDay = fmt(startDate, 'LLL d');
	const endDay = fmt(endDate, 'LLL d');

	return {
		eventName,
		eventId: eventId !== undefined ? String(eventId) : undefined,
		eventStartDate: startDay,
		eventStartTime: fmt(startDate, 'hh:mm a'),
		// Match the backend's zero-value guard: only send an end date/time when it differs, so the
		// template's {{#if eventEndDate}} guards hide them exactly as they do in the real email.
		eventEndDate: endDay && endDay !== startDay ? endDay : '',
		eventEndTime: fmt(endDate, 'hh:mm a'),
		eventTimeZone: timeZone ? timeZone.replace(/_/g, ' ') : '',
		venueName: venueName || '',
		companyLogoUrl: companyLogoUrl || '',
		eventBanner: eventBanner || '',
		primaryColor: primaryColor || '',
		titleColor: titleColor || '',
		webpageUrl: webpageUrl || '',
	};
};
